/*
 * SUSAN — Unified Activity Log API Utility
 * src/utils/activityLogApi.js
 */

const ACTIVITY_LOG_API = 'https://7553ja7ah0.execute-api.ap-southeast-2.amazonaws.com/prod/api/activity-log';

// ── Fetch client IP via SUSAN CloudFront distribution ──────────────
export async function getClientIP() {
  try {
    const res  = await fetch('https://d1oul0ahiu5rh1.cloudfront.net/userlocation');
    const data = await res.json();
    return data.ip;
  } catch (err) {
    console.error('[ActivityLog] Unable to fetch IP:', err);
    return 'unknown';
  }
}

// ── Log a single activity event ────────────────────────────────────
export async function logActivity({
  userData,
  orgId,
  region,
  module,
  category,
  action,
  description,
  severity  = 'info',
  metadata  = {},
  ipAddress = '',
}) {
  try {
    const resolvedRegion = region || sessionStorage.getItem('userRegion') || 'AP';
    const userId         = userData?.['cognito:username'] || userData?.cognitousername || '';
    const actorEmail     = userData?.email || '';
    const actorName      = userData?.name  || userData?.['cognito:username'] || '';

    if (!orgId || !userId) {
      console.warn('[ActivityLog] Missing orgId or userId — skipping log');
      return;
    }

    await fetch(ACTIVITY_LOG_API, {
      method: 'POST',
      headers: {
        'Content-Type':   'application/json',
        'organizationId': orgId,
        'UserId':         userId,
        'Region':         resolvedRegion,
      },
      body: JSON.stringify({
        actorEmail,
        actorName,
        module,
        category,
        action,
        description,
        severity,
        metadata,
        ipAddress,
      }),
    });
  } catch (err) {
    console.error('[ActivityLog] Failed to log activity:', err);
  }
}

// ── Fetch all logs for an org ──────────────────────────────────────
export async function fetchActivityLogs({ orgId, region, filters = {} }) {
  const resolvedRegion = region || sessionStorage.getItem('userRegion') || 'AP';

  const params = new URLSearchParams();
  if (filters.module)      params.append('module',      filters.module);
  if (filters.category)    params.append('category',    filters.category);
  if (filters.severity)    params.append('severity',    filters.severity);
  if (filters.actorUserId) params.append('actorUserId', filters.actorUserId);
  if (filters.from)        params.append('from',        filters.from);
  if (filters.to)          params.append('to',          filters.to);

  const url = `${ACTIVITY_LOG_API}${params.toString() ? '?' + params.toString() : ''}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'organizationId': orgId,
      'Region':         resolvedRegion,
    },
  });

  if (!res.ok) throw new Error(`Activity log fetch failed: ${res.status}`);
  return res.json();
}


// ── Preset helpers ─────────────────────────────────────────────────
export const ActivityPresets = {
  userLogin:    (desc)        => ({ category: 'Authentication', action: 'User Login',       severity: 'success',  description: desc  || 'Successful login via SSO' }),
  userLogout:   (desc)        => ({ category: 'Authentication', action: 'User Logout',      severity: 'info',     description: desc  || 'User signed out' }),
  loginFailed:  (desc)        => ({ category: 'Authentication', action: 'Login Failed',     severity: 'critical', description: desc  || 'Failed login attempt' }),

  policyPublished: (name)     => ({ category: 'Policy', action: 'Policy Published',         severity: 'success',  description: `${name} published to org` }),
  policyUpdated:   (name)     => ({ category: 'Policy', action: 'Policy Updated',           severity: 'info',     description: `${name} updated` }),
  policyDeleted:   (name)     => ({ category: 'Policy', action: 'Policy Deleted',           severity: 'warning',  description: `${name} deleted` }),

  apiKeyGenerated: (desc)     => ({ category: 'Settings', action: 'API Key Generated',      severity: 'warning',  description: desc  || 'New API key created for integration' }),
  settingChanged:  (desc)     => ({ category: 'Settings', action: 'Setting Changed',        severity: 'info',     description: desc }),

  inviteSent:    (email)      => ({ category: 'Invitation', action: 'Invite Sent',          severity: 'info',     description: `Invitation sent to ${email}` }),
  inviteExpired: (email)      => ({ category: 'Invitation', action: 'Invite Expired',       severity: 'warning',  description: `Invitation link expired for ${email}` }),
  inviteAccepted:(email)      => ({ category: 'Invitation', action: 'Invite Accepted',      severity: 'success',  description: `${email} accepted the invitation` }),

  reportGenerated:   (type)   => ({ category: 'Data', action: 'Report Generated',           severity: 'info',     description: `${type} compliance report generated` }),
  dataExported:      (label)  => ({ category: 'Data', action: 'Data Exported',              severity: 'warning',  description: `${label} data exported` }),

  riskUpdated:  (desc)        => ({ category: 'Risk', action: 'Risk Updated',               severity: 'warning',  description: desc  || 'Risk score recalculated after audit review' }),
  riskResolved: (desc)        => ({ category: 'Risk', action: 'Risk Resolved',              severity: 'success',  description: desc }),

  userAdded:    (email)       => ({ category: 'UserManagement', action: 'User Added',       severity: 'success',  description: `${email} added to org` }),
  userRemoved:  (email)       => ({ category: 'UserManagement', action: 'User Removed',     severity: 'warning',  description: `${email} removed from org` }),
  roleChanged:  (email, role) => ({ category: 'UserManagement', action: 'Role Changed',     severity: 'warning',  description: `${email} role changed to ${role}` }),

  complianceScored: (mod, score) => ({ category: 'Compliance', action: 'Score Updated',     severity: score >= 70 ? 'success' : score >= 40 ? 'warning' : 'critical', description: `${mod} compliance score updated to ${score}%` }),
  moduleActivated:  (mod)        => ({ category: 'Compliance', action: 'Module Activated',  severity: 'success',  description: `${mod} module activated` }),
};